/**
 * The looping buffers: room tone, snoring, footfalls at a run, and the grill.
 *
 * A loop is rendered once into an AudioBuffer and then played back on repeat,
 * rather than scheduled note by note the way the one-shots are - a sound that
 * runs for the whole session cannot afford a node per grain. `loopBuffer`
 * renders one and `normalise` keeps them all at a comparable peak.
 */

import { TAU } from './synth';
import type { LoopSound } from './AudioManager';

/** Scales in place so the loudest sample sits at `peak`. Cheap clip guard. */
function normalise(data: Float32Array, peak: number): void {
  let loudest = 0;
  for (let i = 0; i < data.length; i++) loudest = Math.max(loudest, Math.abs(data[i]));
  if (loudest <= 1e-6) return;
  const scale = peak / loudest;
  for (let i = 0; i < data.length; i++) data[i] *= scale;
}

/**
 * Builds a buffer that loops without a click.
 *
 * `fill` is asked for `fade` samples *beyond* the end of the buffer, and those
 * are crossfaded back over the start. The buffer then continues into itself:
 * the sample after the last one is exactly the first one, so there is no step
 * at the loop point for the ear to catch. Modulations are given whole-number
 * cycle counts for the same reason.
 */
function loopBuffer(
  ctx: AudioContext,
  seconds: number,
  fadeSeconds: number,
  fill: (data: Float32Array, rate: number) => void,
): AudioBuffer {
  const rate = ctx.sampleRate;
  const length = Math.max(1, Math.floor(seconds * rate));
  const fade = Math.min(Math.floor(fadeSeconds * rate), Math.floor(length / 4));
  const work = new Float32Array(length + fade);
  fill(work, rate);
  for (let i = 0; i < fade; i++) {
    const w = i / fade;
    work[i] = work[i] * w + work[length + i] * (1 - w);
  }

  const buffer = ctx.createBuffer(1, length, rate);
  const data = buffer.getChannelData(0);
  data.set(work.subarray(0, length));
  normalise(data, 0.85);
  return buffer;
}

/** Short white-noise buffer, looped by every `noiseBurst`. */
export function makeNoiseBuffer(ctx: AudioContext, rng: () => number, seconds = 2): AudioBuffer {
  const buffer = ctx.createBuffer(1, Math.max(1, Math.floor(seconds * ctx.sampleRate)), ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = rng() * 2 - 1;
  return buffer;
}

/**
 * Morning in the yard: birds over silence, and nothing at all underneath them.
 *
 * There used to be a wind bed here - noise through a one-pole lowpass, gusting.
 * It is gone on purpose, and the reason is worth writing down because it is
 * easy to add back. This loop is the one sound that plays for the whole run,
 * from the first step to the last, so anything *continuous* in it is heard for
 * forty seconds without a break - at which point it stops being a yard and
 * starts being a hiss. Worse, the only other continuous sound in the mix is the
 * run loop, and broadband noise sitting at ambience level is exactly what
 * covers up a pair of feet. Two birds and a gap read as a yard; a hiss reads as
 * a fault.
 *
 * So: five calls at fixed times, nothing between them, and the gaps are the
 * point rather than an omission. `loopBuffer` normalises, so the level of the
 * whole thing is set by the loudest call in it.
 */
export function makeMorningAmbience(ctx: AudioContext, rng: () => number): AudioBuffer {
  const seconds = 11;

  /**
   * start, top frequency, notes, seconds a note lasts, gap between them, level.
   *
   * A call rather than a chirp: birds repeat themselves, and one warble on its
   * own is a sound effect. The level is what stands in for how far off the bird
   * is - there is no distance model out here, only quieter and louder.
   */
  const calls: readonly (readonly [number, number, number, number, number, number])[] = [
    [0.5, 2800, 3, 0.1, 0.13, 0.85],
    [2.2, 3400, 2, 0.07, 0.1, 0.4],
    [3.6, 2300, 4, 0.12, 0.16, 0.55],
    [6.4, 3100, 2, 0.09, 0.12, 0.7],
    [8.3, 2600, 3, 0.11, 0.14, 0.35],
  ];

  return loopBuffer(ctx, seconds, 0.4, (data, rate) => {
    for (const [start, top, notes, length, gap, level] of calls) {
      for (let note = 0; note < notes; note++) {
        // Each note a little lower and a little quieter than the one before it,
        // which is what makes a series of them one bird rather than several.
        const pitch = top * Math.pow(0.94, note);
        const from = Math.floor((start + note * (length + gap)) * rate);
        const span = Math.floor(length * rate);
        // The warble *is* the bird: a plain sine at 2.8 kHz is a test tone. Its
        // depth is drawn once per note - drawn per sample it would be noise,
        // which is the thing this loop is deliberately not made of.
        const depth = 0.18 + rng() * 0.1;
        let phase = 0;
        for (let i = 0; i < span && from + i < data.length; i++) {
          const u = i / span;
          const env = Math.sin(Math.PI * u) ** 2 * 0.09 * level * Math.pow(0.88, note);
          const warble = pitch * (1 + depth * Math.sin(TAU * (22 + note * 4) * u));
          phase += (TAU * warble) / rate;
          data[from + i] += Math.sin(phase) * env;
        }
      }
    }
  });
}

/**
 * The juhlasali at nine in the morning: one sleeper, one full breath cycle.
 * The rasp is amplitude modulation, which is all a snore really is.
 */
export function makeSnoringLoop(ctx: AudioContext, rng: () => number): AudioBuffer {
  // start, length, loudness, rasp rate. The inhale is the loud one.
  const breaths: readonly (readonly [number, number, number, number])[] = [
    [0.15, 1.15, 1.0, 27],
    [2.0, 0.95, 0.45, 19],
  ];

  return loopBuffer(ctx, 4.4, 0.15, (data, rate) => {
    let low = 0;
    for (let i = 0; i < data.length; i++) {
      const t = i / rate;
      low += (rng() * 2 - 1 - low) * 0.06;
      let sample = 0;
      for (const [start, length, loudness, rasp] of breaths) {
        if (t < start || t >= start + length) continue;
        const u = (t - start) / length;
        const env = Math.sin(Math.PI * u) ** 2 * loudness;
        sample += low * env * (0.45 + 0.55 * Math.sin(TAU * rasp * (t - start)));
      }
      data[i] = sample * 3.0;
    }
  });
}

/**
 * Somebody running, at the pace the recording of somebody running runs at.
 *
 * The fallback for `running` when the folder has no take of it, so that the
 * promise at the top of config/audio.ts still holds: an empty samples folder is
 * a game that still makes every one of its sounds. It is the `footstep` recipe
 * written out in raw samples rather than in nodes, because a loop is a buffer
 * and a buffer has to be filled in one pass.
 *
 * Eight steps rather than two: the alternation between a heavier foot and a
 * lighter one is most of what stops a run reading as a metronome, and at two
 * steps the ear finds the loop point immediately.
 */
export function makeRunningLoop(ctx: AudioContext, rng: () => number): AudioBuffer {
  const step = 0.385;
  const steps = 8;
  return loopBuffer(ctx, step * steps, 0.12, (data, rate) => {
    for (let s = 0; s < steps; s++) {
      // The weight lands on one foot harder than the other, and nobody is a
      // metronome, so each step is nudged off the grid by a few milliseconds.
      const weight = s % 2 === 0 ? 1 : 0.78;
      const at = Math.floor((s * step + (rng() - 0.5) * 0.012) * rate);
      const pitch = 0.85 + rng() * 0.3;

      // The heel: a tone dropping most of an octave as the foot flattens out.
      const thump = Math.floor(0.09 * rate);
      let phase = 0;
      for (let i = 0; i < thump && at + i < data.length; i++) {
        const u = i / thump;
        phase += (TAU * (92 * pitch * Math.pow(58 / (92 * pitch), u))) / rate;
        data[at + i] += Math.sin(phase) * Math.exp(-u * 5.5) * 0.5 * weight;
      }

      // The grit under it: noise through a one-pole lowpass that closes as the
      // shoe settles, which is the same sweep `footstep` asks a biquad for.
      const scuff = Math.floor(0.11 * rate);
      let low = 0;
      for (let i = 0; i < scuff && at + i < data.length; i++) {
        const u = i / scuff;
        low += (rng() * 2 - 1 - low) * (0.22 - 0.16 * u);
        data[at + i] += low * Math.exp(-u * 6) * 0.85 * weight;
      }
    }
  });
}

/**
 * A metre of steel plate with breakfast on it, out on the kivetys.
 *
 * The one loop in the game a synthesiser is actually *good* at, and the reason
 * is the same one that makes `ambient-morning` argue the opposite way: fat in a
 * pan is broadband noise with no pitch and no rhythm in it, which is the exact
 * thing two oscillators cannot do and a noise generator does for free. So this
 * is not a stand-in that is worse than the recording in kind - it is worse than
 * it only in the detail of the spitting.
 *
 * Two parts, which is all a sizzle is:
 *
 *   * The bed. White noise with a one-pole lowpass subtracted from it, i.e. a
 *     6 dB/octave highpass, because the take measured its weight up at ten
 *     kilohertz and 34 dB down at sixty hertz. Two slow wobbles over it at
 *     whole-number cycle counts, because a pan does not hiss at one level and
 *     because an unmodulated noise bed is a fault rather than a grill.
 *   * The spitting. A dozen short bursts a loop, each a few dB over the bed,
 *     which is the part that says *fat* rather than *tape hiss* - the take's
 *     own crest factor is 22 dB, and almost all of it is these.
 */
export function makeGrillLoop(ctx: AudioContext, rng: () => number): AudioBuffer {
  const seconds = 6;
  const spits = 14;

  return loopBuffer(ctx, seconds, 0.3, (data, rate) => {
    // 2.6 kHz, which is where the recording crosses from "nothing there" to the
    // slope that carries the rest of it.
    const corner = 1 - Math.exp((-TAU * 2600) / rate);
    let low = 0;
    for (let i = 0; i < data.length; i++) {
      const white = rng() * 2 - 1;
      low += (white - low) * corner;
      const u = i / (seconds * rate);
      const wobble = 1 + 0.2 * Math.sin(TAU * 3 * u) + 0.12 * Math.sin(TAU * 7 * u + 1.1);
      data[i] = (white - low) * 0.16 * wobble;
    }

    // Scattered rather than on a grid: a grid of these is a rattle, and the one
    // thing the ear picks a loop point out of noise by is a spit that comes
    // round on the beat.
    for (let spit = 0; spit < spits; spit++) {
      const at = Math.floor((spit + rng()) * (seconds / spits) * rate);
      const span = Math.floor((0.008 + rng() * 0.02) * rate);
      const level = 0.35 + rng() * 0.5;
      let hiss = 0;
      for (let i = 0; i < span && at + i < data.length; i++) {
        const white = rng() * 2 - 1;
        // A brighter filter than the bed's, and it closes as the drop settles:
        // a spit starts as a crack and ends as part of the hiss it came out of.
        hiss += (white - hiss) * (0.5 - 0.3 * (i / span));
        data[at + i] += (white - hiss) * Math.exp((-i / span) * 4) * level;
      }
    }
  });
}

/**
 * The synthesised version of a loop, or null for one that has none.
 *
 * `karaoke` is the null: it is a person singing into a PA, and there is no
 * arrangement of oscillators that is better than waiting for the recording. A
 * loop with no buffer simply does not play - see `WebAudioManager.syncLoops`.
 */
export function makeLoopBuffer(
  ctx: AudioContext,
  sound: LoopSound,
  rng: () => number,
): AudioBuffer | null {
  switch (sound) {
    case 'ambient-morning':
      return makeMorningAmbience(ctx, rng);
    case 'ambient-snoring':
      return makeSnoringLoop(ctx, rng);
    case 'running':
      return makeRunningLoop(ctx, rng);
    case 'muurikka':
      return makeGrillLoop(ctx, rng);
    // The two that have no synthesised version and never will. One is somebody
    // at a microphone and the other is a track off a laptop; either is a
    // recording or it is silence, and there is nothing in a pair of
    // oscillators that approximates either.
    case 'karaoke':
    case 'teltta-dj':
      return null;
  }
}
